import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ERM_ICONS_ENUM } from '@erm/cluster';

import {
  FLOAT_CONFIG_PANEL_GROUP_ICON_MAPPER,
  FLOAT_CONFIG_PANEL_ICON_MAPPER,
} from '../../constants';
import {
  ConfigMapper,
  ConfigPanel,
  ConfigPanelStateKey,
  DashboardConfigComponentType,
  FloatConfigStripGroup,
  FloatConfigStripItem,
} from '../../types';

/**
 * Floating, draggable dev-tool panel for the dashboard landing page.
 *
 * Collapsed, it is a single fab; open, a narrow vertical strip shows a reset
 * fab on top and one accordion group per data-driven dashboard card. A group
 * header is the card's own icon (name in the tooltip); clicking it expands
 * the card's four state fabs and collapses any other open group.
 *
 * A card's state is single-select ({@link ConfigPanel.currentState}): the
 * empty/loading/error fabs select their state (clicking the active one
 * returns the card to data mode), and the mock fab flips the mock flag while
 * returning to data mode. Each fab is themed to match how its state renders
 * inside the cards and dims while inactive.
 *
 * Pure presentational component: it owns no data state and injects nothing.
 * The consumer supplies the rows through
 * {@link FloatConfigPanelComponent.panels} and reacts to
 * {@link FloatConfigPanelComponent.mockToggled}.
 *
 * @example
 * ```html
 * <wcg-float-config-panel
 *   [panels]="$configPanels()"
 *   (mockToggled)="onMockToggled($event)"
 * />
 * ```
 */
@Component({
  selector: 'wcg-float-config-panel',
  imports: [
    CdkDrag,
    CdkDragHandle,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './float-config-panel.component.html',
  styleUrls: ['./float-config-panel.component.css'],
})
export class FloatConfigPanelComponent {
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Whether the panel is collapsed down to just the fab. */
  protected readonly $minimized = signal(true);

  /** Icon enum exposed for the sync and reset fabs. */
  protected readonly icons = ERM_ICONS_ENUM;

  /** Key of the single expanded group; null when all are collapsed. */
  protected readonly $expandedPanelKey =
    signal<DashboardConfigComponentType | null>(null);

  /** Rows to render — one per data-driven dashboard card. */
  readonly panels = input.required<ConfigMapper>();

  /** Emits a fresh config map carrying the flipped state's new value. */
  readonly mockToggled = output<ConfigMapper>();

  /** Emits when the reset fab is pressed; the consumer restores its defaults. */
  readonly mockReset = output<void>();

  /**
   * Strip view-model: one group per dashboard card, one fab per mockable
   * state, both in map insertion order.
   */
  protected readonly $stripGroups = computed<FloatConfigStripGroup[]>(() =>
    Array.from(this.panels(), ([panelKey, panel]) => ({
      panelKey,
      name: panel.name,
      icon:
        FLOAT_CONFIG_PANEL_GROUP_ICON_MAPPER.get(panelKey) ??
        ERM_ICONS_ENUM.SYNC,
      themeClass: `wcg-float-config-panel__group-header--${panelKey}`,
      expanded: this.$expandedPanelKey() === panelKey,
      items: Array.from(
        FLOAT_CONFIG_PANEL_ICON_MAPPER,
        ([stateKey, icon]): FloatConfigStripItem => ({
          stateKey,
          icon,
          active:
            stateKey === 'toggleMock'
              ? panel.currentState === 'toggleMock' && panel.toggleMock
              : panel.currentState === stateKey,
          tooltip: stateKey,
          ariaLabel: `${panel.name} · ${stateKey}`,
          themeClass: `wcg-float-config-panel__fab-btn--${stateKey}`,
        }),
      ),
    })),
  );

  /** Collapses the panel to the fab, or restores the strip. */
  protected toggleMinimized(): void {
    this.$minimized.update((minimized) => !minimized);
  }

  /**
   * Expands the clicked group and collapses the previously open one, or
   * collapses the clicked group when it is already expanded.
   *
   * @param panelKey - The card group whose header was clicked.
   */
  protected handleGroupExpand(panelKey: DashboardConfigComponentType): void {
    this.$expandedPanelKey.update((expanded) =>
      expanded === panelKey ? null : panelKey,
    );
  }

  /**
   * Applies a fab press to one card and emits the result.
   *
   * The empty/loading/error fabs select their state; pressing the already
   * active one returns the card to data mode. The mock fab flips the mock
   * flag and returns the card to data mode. Emits a new map with a new row
   * object instead of mutating the input: signals compare by reference, so
   * re-emitting the consumer's own map would not re-run its computeds.
   *
   * @param panelKey - The card whose fab was pressed.
   * @param stateKey - The state the fab selects.
   */
  protected handleToggleState(
    panelKey: DashboardConfigComponentType,
    stateKey: ConfigPanelStateKey,
  ): void {
    const _current = this.panels().get(panelKey);
    if (!_current) {
      return;
    }

    const _row: ConfigPanel =
      stateKey === 'toggleMock'
        ? {
            ..._current,
            currentState: 'toggleMock',
            toggleMock: !_current.toggleMock,
          }
        : {
            ..._current,
            currentState:
              _current.currentState === stateKey ? 'toggleMock' : stateKey,
          };

    const _next: ConfigMapper = new Map(this.panels());
    _next.set(panelKey, _row);
    this.mockToggled.emit(_next);
  }

  /**
   * Relays the reset fab press to the consumer, which owns the default
   * configuration and restores it.
   */
  protected handleReset(): void {
    this.mockReset.emit();
  }

  /**
   * Minimizes the open panel when the user clicks outside it. Clicks inside
   * the panel are excluded by the containment check.
   *
   * @param event - The document-level click event.
   */
  @HostListener('document:click', ['$event'])
  protected _onDocumentClick(event: MouseEvent): void {
    if (this.$minimized()) {
      return;
    }
    const _target = event.target as Node | null;
    if (_target && !this._elementRef.nativeElement.contains(_target)) {
      this.$minimized.set(true);
    }
  }
}
